import { useEffect, useState } from 'react';
import { IDLE_LIMIT_MS, IDLE_LOGOUT_NOTICE_KEY } from '@/hooks/useIdleLogout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabaseConfigured } from '@/services/supabaseClient';
import './login.css';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTestAccess, setShowTestAccess] = useState(false);
  // Mostra o aviso uma única vez quando o sistema desconectou por inatividade.
  const [idleNotice] = useState(() => { try { return sessionStorage.getItem(IDLE_LOGOUT_NOTICE_KEY) === '1'; } catch { return false; } });
  useEffect(() => { try { sessionStorage.removeItem(IDLE_LOGOUT_NOTICE_KEY); } catch { /* ignora */ } }, []);
  const { login } = useAuth();
  const navigate = useNavigate();

  const access = async (loginEmail: string, loginPassword: string) => {
    setError('');
    setLoading(true);
    const err = await login(loginEmail, loginPassword);
    if (err) {
      setError(err);
      setLoading(false);
      return;
    }
    sessionStorage.setItem('zsb_show_coffee_transition', '1');
    window.dispatchEvent(new Event('zsb-login-success'));
    navigate('/', { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await access(email, password);
  };

  const handleQuickLogin = async (loginEmail: string) => {
    setEmail(loginEmail);
    setPassword('123456');
    await access(loginEmail, '123456');
  };

  return (
    <div className="route-page-enter zsb-login">
      <div className="zsb-login__shell">
        <div className="zsb-login__visual" aria-hidden="true">
          <img src="/brand/login-laboratory.png" alt="" className="zsb-login__image" fetchPriority="high" />
        </div>

        <section className="zsb-login__form-panel" aria-labelledby="login-title">
          <div className="zsb-login__form">
            <div className="mb-8">
              <div className="zsb-login__brand"><img src="/brand/zschimmer-mark.png" alt="Zschimmer & Schwarz" /><div><p>ZSCHIMMER & SCHWARZ</p><span>BRASIL</span></div></div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">ZSB Calculator</p>
              <h1 id="login-title" className="mt-3 font-heading text-3xl font-bold tracking-tight text-primary-900">Bem-vindo de volta</h1>
              <p className="mt-1 text-sm text-foreground-600">Entre com suas credenciais para continuar.</p>
            </div>

        <form onSubmit={handleSubmit}>
          {idleNotice && !error && <div role="status" className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">Você foi desconectado por ficar {IDLE_LIMIT_MS / 60000} minutos sem usar o sistema. Entre novamente para continuar.</div>}
          {error && <div role="alert" className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">{error}</div>}
          <div className="mb-5">
            <label htmlFor="login-email" className="block text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-1.5">E-mail</label>
            <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              placeholder="nome@empresa.com.br" className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-sm text-foreground-950 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-500" />
          </div>
          <div className="mb-5">
            <label htmlFor="login-password" className="block text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-1.5">Senha</label>
            <div className="relative"><input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
              placeholder="Digite sua senha" className="w-full px-4 py-3 pr-12 rounded-xl border border-background-300 bg-white text-sm text-foreground-950 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-500" /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 w-12 text-foreground-500 hover:text-primary-600" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}><i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} /></button></div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-3 px-4 rounded-xl bg-primary-600 text-white text-sm font-semibold shadow-lg shadow-primary-900/15 hover:bg-primary-700 transition-all disabled:opacity-50">
            {loading ? <><i className="ri-loader-4-line animate-spin mr-2" />Aguarde...</> : <>Entrar <i className="ri-arrow-right-line ml-1" /></>}
          </button>
        </form>
        <p className="zsb-login__access-note"><i className="ri-lock-line" aria-hidden="true" />Acesso exclusivo para usuários autorizados.</p>

        {!supabaseConfigured && <div className="mt-6 border-t border-background-200 pt-4">
          <button type="button" onClick={() => setShowTestAccess((value) => !value)} className="flex w-full items-center justify-between text-xs font-semibold text-foreground-600 hover:text-primary-700"><span><i className="ri-user-settings-line mr-2" />Acessos de demonstração</span><i className={showTestAccess ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} /></button>
          {showTestAccess && <div className="mt-3 rounded-xl bg-background-50 p-3"><p className="text-xs text-foreground-600 mb-2">Senha para todos: <strong>123456</strong></p><div className="grid gap-2">
            {[
              ['Administrador', 'admin@zsb.com.br'],
              ['Gerente', 'gerente@zsb.com.br'],
              ['Vendedor', 'carla@zsb.com.br'],
            ].map(([role, accountEmail]) => (
              <button key={accountEmail} type="button" onClick={() => handleQuickLogin(accountEmail)} disabled={loading}
                className="w-full p-2.5 rounded-lg bg-white border border-background-200 text-left hover:border-primary-400 hover:bg-primary-50/40 disabled:opacity-50">
                <span className="block text-xs font-semibold text-foreground-950">Acessar como {role}</span>
                <span className="block text-xs text-foreground-600">{accountEmail}</span>
              </button>
            ))}
          </div></div>}
        </div>}
          </div>
        </section>
      </div>
    </div>
  );
}
